import { MODEL_FIXTURES, PROFILE_FIXTURES, THROUGHPUT_FIXTURES } from './catalogFixtures';
import type {
  Accelerator,
  CatalogData,
  Model,
  ProfileSeed,
  SizingConfig,
  ThroughputPoint,
} from '../types';

export const FALLBACK_ACCELERATOR: Accelerator = {
  accelerator_id: 'gaudi3',
  backend: 'gaudi3_pcie',
  hbm_bytes: 128 * 1024 ** 3,
  reserved_runtime_bytes: 8 * 1024 ** 3,
  bridge_group_size_max: 4,
  display_name: 'Intel Gaudi3',
  meta: null,
};

export const FALLBACK_SERVER: CatalogData['server'] = {
  server_sku: 'XE7740',
  accelerator_id: 'gaudi3',
  accel_slots: 8,
  display_name: 'Dell PowerEdge XE7740',
  dell_link_url: 'https://www.dell.com/xe7740',
  meta: null,
};

export const FALLBACK_CONFIG: SizingConfig = {
  id: true,
  memory_headroom_pct: 0.2,
  throughput_headroom_pct: 0.2,
};

export function cloneModel(model: Model): Model {
  return {
    ...model,
    weights_mem_json: { ...model.weights_mem_json },
    kv_per_token_json: { ...model.kv_per_token_json },
    meta: model.meta ? { ...model.meta } : model.meta,
  };
}

export function cloneProfiles(profiles: ProfileSeed[]): ProfileSeed[] {
  return profiles.map((profile) => ({ ...profile }));
}

export function buildFallbackModels(): Model[] {
  return MODEL_FIXTURES.map(cloneModel).sort((a, b) => a.display_name.localeCompare(b.display_name));
}

export function buildFallbackThroughput(): ThroughputPoint[] {
  return THROUGHPUT_FIXTURES.map((point) => ({ ...point })).sort((a, b) => {
    if (a.model_id !== b.model_id) {
      return a.model_id.localeCompare(b.model_id);
    }
    if (a.precision !== b.precision) {
      return a.precision - b.precision;
    }
    return a.batch - b.batch;
  });
}

export function buildFallbackProfiles(): ProfileSeed[] {
  return cloneProfiles(PROFILE_FIXTURES).sort((a, b) => a.display_name.localeCompare(b.display_name));
}

export function buildFallbackCatalog(): CatalogData {
  return {
    accelerator: { ...FALLBACK_ACCELERATOR },
    server: { ...FALLBACK_SERVER },
    models: buildFallbackModels(),
    throughput: buildFallbackThroughput(),
    profiles: buildFallbackProfiles(),
    config: { ...FALLBACK_CONFIG },
  } satisfies CatalogData;
}
